// "Pause timings" for a campaign: up to 3 daily time slots (same time every day the
// campaign runs) during which the scheduler pauses dialing. Only the times are saved
// (campaign_schedule.pause_slots); the scheduler does the pausing.
//
// A slot must sit inside the campaign's daily window (see getPauseWindow), last at
// least 10 minutes and not overlap another slot.
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, ArrowRight, Clock, Pause, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CalendarTimeInput } from '@/components/ui/calendar-inputs';
import { Input } from '@/components/ui/input';

export interface PauseSlot {
  name?: string; // optional label, e.g. "Tea break"
  start_time: string; // "HH:mm"
  end_time: string; // "HH:mm"
}

export interface PauseWindow {
  start: string; // "HH:mm"
  end: string; // "HH:mm"
  label: string;
}

export const MAX_PAUSE_SLOTS = 3;
export const MIN_PAUSE_MINUTES = 10;

/** Display label: the slot's own name, or "Pause N". */
export const pauseLabel = (slot: PauseSlot, index: number) => slot.name?.trim() || `Pause ${index + 1}`;

const toMin = (t: string | null | undefined): number | null => {
  if (!t || !/^\d{2}:\d{2}$/.test(t)) return null;
  const [h = 0, m = 0] = t.split(':').map(Number);
  return h * 60 + m;
};
const toTime = (mins: number) =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
const fmtDuration = (mins: number) =>
  mins >= 60 ? `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ''}` : `${mins}m`;

/**
 * Daily window a pause slot must fit in:
 *  - strict timings on, or a one-day campaign -> the chosen From–To hours
 *  - strict off: promotional 09:00–21:00, transactional the whole day (00:00–23:59)
 * Returns null while the schedule is incomplete.
 */
export function getPauseWindow(opts: {
  strict: boolean;
  promotional: boolean;
  startDate?: string | null;
  endDate?: string | null;
  startHour?: string | null;
  endHour?: string | null;
}): PauseWindow | null {
  const { strict, promotional, startDate, endDate, startHour, endHour } = opts;
  if (strict || (startDate && endDate && startDate === endDate)) {
    if (toMin(startHour) == null || toMin(endHour) == null) return null;
    return { start: startHour as string, end: endHour as string, label: `${startHour}–${endHour} (campaign hours)` };
  }
  return promotional
    ? { start: '09:00', end: '21:00', label: '09:00–21:00 (9 to 21, promotional)' }
    : { start: '00:00', end: '23:59', label: '00:00–23:59 (all day, transactional)' };
}

/** One message per slot ('' when valid), checked against the window and each other. */
export function validatePauseSlots(slots: PauseSlot[], window: PauseWindow | null): string[] {
  const wStart = toMin(window?.start);
  const wEnd = toMin(window?.end);
  return slots.map((s, i) => {
    const a = toMin(s.start_time);
    const b = toMin(s.end_time);
    if (a == null || b == null) return 'Select both From and To.';
    if (b <= a) return 'To must be after From.';
    if (b - a < MIN_PAUSE_MINUTES) return `A pause must be at least ${MIN_PAUSE_MINUTES} minutes.`;
    if (wStart == null || wEnd == null) return 'Set the campaign schedule first.';
    if (a < wStart || b > wEnd) return `Must be within ${window?.start}–${window?.end}.`;
    const clash = slots.some((o, j) => {
      if (j === i) return false;
      const oa = toMin(o.start_time);
      const ob = toMin(o.end_time);
      return oa != null && ob != null && a < ob && oa < b;
    });
    return clash ? 'Overlaps another pause.' : '';
  });
}

/** First free gap in the window for a new slot, sized to the 10-minute minimum. */
function nextFreeSlot(slots: PauseSlot[], window: PauseWindow | null): PauseSlot | null {
  const wStart = toMin(window?.start);
  const wEnd = toMin(window?.end);
  if (wStart == null || wEnd == null) return null;
  const taken = slots
    .map((s) => [toMin(s.start_time), toMin(s.end_time)])
    .filter((p): p is [number, number] => p[0] != null && p[1] != null)
    .sort((x, y) => x[0] - y[0]);
  let cursor = wStart;
  const gaps: [number, number][] = [];
  for (const [a, b] of taken) {
    if (a > cursor) gaps.push([cursor, a]);
    cursor = Math.max(cursor, b);
  }
  if (wEnd > cursor) gaps.push([cursor, wEnd]);
  const gap = gaps.find(([a, b]) => b - a >= MIN_PAUSE_MINUTES);
  if (!gap) return null;
  const len = MIN_PAUSE_MINUTES;
  return { name: '', start_time: toTime(gap[0]), end_time: toTime(gap[0] + len) };
}

export default function PauseSlotsModal({
  open,
  onOpenChange,
  slots,
  window,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slots: PauseSlot[];
  window: PauseWindow | null;
  onSave: (slots: PauseSlot[]) => void;
}) {
  const [draft, setDraft] = useState<PauseSlot[]>(slots);
  useEffect(() => {
    if (open) setDraft(slots);
  }, [open, slots]);

  const errors = useMemo(() => validatePauseSlots(draft, window), [draft, window]);
  const hasErrors = errors.some(Boolean);
  const wStart = toMin(window?.start);
  const wEnd = toMin(window?.end);
  const span = wStart != null && wEnd != null && wEnd > wStart ? wEnd - wStart : null;
  const totalPause = draft.reduce((sum, s) => {
    const a = toMin(s.start_time);
    const b = toMin(s.end_time);
    return a != null && b != null && b > a ? sum + (b - a) : sum;
  }, 0);
  const candidate = nextFreeSlot(draft, window);
  const canAdd = draft.length < MAX_PAUSE_SLOTS && Boolean(candidate);

  const update = (i: number, patch: Partial<PauseSlot>) =>
    setDraft((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const remove = (i: number) => setDraft((prev) => prev.filter((_, j) => j !== i));
  const add = () => candidate && setDraft((prev) => [...prev, candidate]);
  const save = () => {
    const sorted = [...draft]
      .map((s) => ({ ...s, name: (s.name || '').trim() }))
      .sort((x, y) => (toMin(x.start_time) ?? 0) - (toMin(y.start_time) ?? 0));
    onSave(sorted);
    onOpenChange(false);
  };

  const pct = (t: string) => {
    const m = toMin(t);
    if (m == null || span == null || wStart == null) return 0;
    return Math.min(100, Math.max(0, ((m - wStart) / span) * 100));
  };
  // Round-time ticks across the window: 30 min for short windows, up to 3 h for a full day.
  const ticks = useMemo(() => {
    if (span == null || wStart == null || wEnd == null) return [] as { at: number; label: string }[];
    const step = span <= 180 ? 30 : span <= 480 ? 60 : span <= 960 ? 120 : 180;
    const out: { at: number; label: string }[] = [];
    for (let m = Math.ceil(wStart / step) * step; m <= wEnd; m += step) {
      out.push({ at: ((m - wStart) / span) * 100, label: toTime(m) });
    }
    return out;
  }, [span, wStart, wEnd]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-3 sm:max-w-xl">
        <DialogHeader className="space-y-1">
          <DialogTitle className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <Pause className="h-4 w-4" />
            </span>
            Pause timings
          </DialogTitle>
          <DialogDescription className="text-[13px] leading-snug">
            The campaign won't dial during these times, every day it runs.
          </DialogDescription>
        </DialogHeader>

        {window ? (
          <div className="space-y-4">
            {/* ── Day overview: the campaign's daily window with the pause blocks on it ── */}
            <div className="rounded-xl border border-border/60 bg-muted/30 p-3.5">
              <div className="mb-3 flex flex-wrap items-center gap-2 text-[11px]">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-background px-2.5 py-1 font-medium text-foreground shadow-sm">
                  <Clock className="h-3 w-3 text-primary" /> {window.label}
                </span>
                <span className="inline-flex items-center rounded-full bg-background px-2.5 py-1 font-medium tabular-nums text-foreground shadow-sm">
                  {totalPause ? `${fmtDuration(totalPause)} paused / day` : 'No pauses'}
                </span>
                {span != null && totalPause ? (
                  <span className="inline-flex items-center rounded-full bg-background px-2.5 py-1 font-medium tabular-nums text-muted-foreground shadow-sm">
                    {fmtDuration(Math.max(0, span - totalPause))} dialing
                  </span>
                ) : null}
              </div>

              <div className="relative h-11 rounded-lg bg-primary/15 ring-1 ring-inset ring-primary/20">
                {ticks.map((t) => (
                  <span
                    key={t.label}
                    className="absolute inset-y-0 w-px bg-primary/15"
                    style={{ left: `${t.at}%` }}
                    aria-hidden
                  />
                ))}
                {draft.map((s, i) => {
                  const left = pct(s.start_time);
                  const width = Math.max(0, pct(s.end_time) - left);
                  if (width <= 0) return null;
                  return (
                    <div
                      key={i}
                      title={`${pauseLabel(s, i)}: ${s.start_time}–${s.end_time}`}
                      className={`absolute inset-y-1.5 flex items-center justify-center rounded-md text-[11px] font-bold text-white shadow-sm ${
                        errors[i] ? 'bg-destructive' : 'bg-amber-500'
                      }`}
                      style={{ left: `${left}%`, width: `${width}%`, minWidth: 8 }}
                    >
                      {width > 4 ? i + 1 : null}
                    </div>
                  );
                })}
              </div>
              <div className="relative mt-1.5 h-4 text-[10.5px] tabular-nums text-muted-foreground">
                <span className="absolute left-0">{window.start}</span>
                {ticks
                  // Skip labels next to the start/end labels so they never collide.
                  .filter((t) => t.at > 12 && t.at < 88)
                  .map((t) => (
                    <span key={t.label} className="absolute -translate-x-1/2" style={{ left: `${t.at}%` }}>
                      {t.label}
                    </span>
                  ))}
                <span className="absolute right-0">{window.end}</span>
              </div>
              <div className="mt-2 flex items-center gap-4 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-primary/30" /> Dialing
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm bg-amber-500" /> Paused
                </span>
              </div>
            </div>

            {/* ── One row per pause ── */}
            {draft.length === 0 ? (
              <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-border px-4 py-6 text-center">
                <Pause className="h-5 w-5 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">No pauses yet</p>
                <p className="text-xs text-muted-foreground">The campaign dials through its whole window.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {draft.map((s, i) => {
                  const a = toMin(s.start_time);
                  const b = toMin(s.end_time);
                  const ok = !errors[i];
                  return (
                    <div
                      key={i}
                      className={`rounded-xl border bg-card px-3 py-2.5 shadow-sm ${
                        ok ? 'border-border/60' : 'border-destructive/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${
                            ok ? 'bg-amber-500' : 'bg-destructive'
                          }`}
                        >
                          {i + 1}
                        </span>
                        <Input
                          value={s.name || ''}
                          onChange={(e) => update(i, { name: e.target.value })}
                          placeholder={`Pause ${i + 1} name (e.g. Tea break)`}
                          aria-label={`Pause ${i + 1} name`}
                          className="h-9 min-w-0 flex-1 text-sm"
                        />
                        <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold tabular-nums text-muted-foreground">
                          {a != null && b != null && b > a ? fmtDuration(b - a) : '—'}
                        </span>
                        <button
                          type="button"
                          onClick={() => remove(i)}
                          aria-label={`Remove pause ${i + 1}`}
                          className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 pl-10">
                          <CalendarTimeInput
                            value={s.start_time}
                            onChange={(v: string) => update(i, { start_time: v })}
                            min={window.start}
                            max={window.end}
                            aria-label={`Pause ${i + 1} from`}
                            className="h-9 w-[112px]"
                          />
                          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                          <CalendarTimeInput
                            value={s.end_time}
                            onChange={(v: string) => update(i, { end_time: v })}
                            min={window.start}
                            max={window.end}
                            aria-label={`Pause ${i + 1} to`}
                            className="h-9 w-[112px]"
                          />
                      </div>
                      {errors[i] ? (
                        <p className="mt-2 flex items-center gap-1.5 pl-10 text-xs font-medium text-destructive">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {errors[i]}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}

            <button
              type="button"
              onClick={add}
              disabled={!canAdd}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-primary/40 px-3 py-2.5 text-sm font-medium text-primary transition-colors hover:bg-primary/5 disabled:cursor-not-allowed disabled:border-border disabled:text-muted-foreground disabled:hover:bg-transparent"
            >
              <Plus className="h-4 w-4" />
              {draft.length >= MAX_PAUSE_SLOTS ? `Maximum ${MAX_PAUSE_SLOTS} pauses` : 'Add pause'}
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] tabular-nums text-muted-foreground">
                {draft.length}/{MAX_PAUSE_SLOTS}
              </span>
            </button>
            <p className="-mt-2 text-center text-[12px] text-muted-foreground">
              Add up to {MAX_PAUSE_SLOTS} pauses of at least {MIN_PAUSE_MINUTES} minutes each.
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-3 text-sm text-amber-800 dark:text-amber-300">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            Set the campaign dates and From / To hours first, then add pause timings.
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={save} disabled={!window || hasErrors}>
            Save pause timings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
